/**
 * Dashboard RPA (`/rpa`, `RpaDashboardView` của native): lọc bảng lượt chạy, thời gian thực hiện, cột biểu đồ theo
 * ngày và theo Project. Số tổng (KPI, tỷ lệ) máy chủ đã tính (`modules/rpa/backend/src/tong-quan/dashboard.ts`), ở
 * đây chỉ chia lát để vẽ. Hàm thuần, không DOM.
 */
import type { DemLuot, InstanceTongQuan } from '../types.ts';

/** Khoảng ngày cho chọn như native; máy chủ nhận 1–365. */
export const KHOANG_NGAY_TONG_QUAN = [7, 14, 30] as const;
export const SO_NGAY_MAC_DINH = 14;

/** Trạng thái lọc được ở bảng lượt chạy (thứ tự như native). */
export const TRANG_THAI_LOC_TONG_QUAN = ['completed', 'failed', 'aborted', 'running', 'idle'] as const;

/** Robot hiện ở ô Tình trạng Robot. */
export const SO_ROBOT_HIEN = 12;
export const TRANG_LUOT_TONG_QUAN = 10;

/** `running`/`idle` là đang chạy (`DANG_CHAY` của backend). */
export const dangChay = (status: string) => status === 'running' || status === 'idle';

/** Thời gian thực hiện (ms) của lượt đã kết thúc; lượt đang chạy hoặc thiếu mốc trả `null`. */
export function thoiLuongMs(i: Pick<InstanceTongQuan, 'status' | 'createdAt' | 'updatedAt'>): number | null {
  if (dangChay(i.status) || !i.createdAt || !i.updatedAt) return null;
  const ms = Date.parse(i.updatedAt) - Date.parse(i.createdAt);
  return Number.isFinite(ms) && ms >= 0 ? ms : null;
}

/** `1 giờ 2 phút`, `3 phút 5 giây`, `12 giây` dưới dạng số để phía gọi ghép chữ theo ngôn ngữ. */
export function tachThoiLuong(ms: number): { gio: number; phut: number; giay: number } {
  const s = Math.round(ms / 1000);
  return { gio: Math.floor(s / 3600), phut: Math.floor((s % 3600) / 60), giay: s % 60 };
}

export interface BoLocTongQuan {
  tim: string;
  status: string;
}

/** Tìm trên Project, Workflow, máy, lỗi; lọc trạng thái — ở máy khách, như native. */
export function locInstance(ds: readonly InstanceTongQuan[], loc: BoLocTongQuan): InstanceTongQuan[] {
  const q = loc.tim.trim().toLowerCase();
  return ds.filter((i) => {
    if (loc.status && i.status !== loc.status) return false;
    if (!q) return true;
    return [i.projectName, i.projectId, i.name, i.workflowId, i.machineName, i.errorMessage, i.instanceId]
      .join(' ')
      .toLowerCase()
      .includes(q);
  });
}

/** Lát của một cột: thành công, lỗi (gồm timeout), đang chạy, còn lại (bị dừng tay) — cộng lại đúng `total`. */
export function latLuot(d: DemLuot): [number, number, number, number] {
  const loi = d.failed + d.timeout;
  return [d.success, loi, d.running, Math.max(0, d.total - d.success - loi - d.running)];
}

/** Tỷ lệ phần trăm làm tròn một chữ số thập phân. */
export const phanTram = (x: number) => Math.round(x * 10) / 10;

/** Dòng CSV: InstanceId, bắt đầu, kết thúc, Project, Workflow, máy, trạng thái, thời gian ms, lỗi (như native). */
export function dongCsvTongQuan(ds: readonly InstanceTongQuan[], gio: (iso: string | null) => string): (string | number)[][] {
  return ds.map((i) => [
    i.instanceId || i.id,
    gio(i.createdAt),
    dangChay(i.status) ? '' : gio(i.updatedAt),
    i.projectName || i.projectId,
    i.name || i.workflowId,
    i.machineName,
    i.status,
    thoiLuongMs(i) ?? '',
    i.errorMessage,
  ]);
}
